<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRef,
} from 'vue';
import { useRoute } from 'vue-router';

import type { ITableMultiselectActionParams } from '@modules/tables/components/VTable/types';
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions, ITableMultiselectActions } from '@modules/tables/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME } from '@modules/affiliates-approved/constants';
import VAffiliateTrackersSettingsTabs from '@modules/affiliates-approved/submodules/trackers-settings/components/VAffiliateTrackersSettingsTabs/VAffiliateTrackersSettingsTabs.vue';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS } from '@modules/affiliates-approved/submodules/trackers-settings/constants';
import {
  ADMIN_AFFILIATE_TRACKERS_FIXED_PAYMENTS_MAIN_ROUTE_NAME,
  AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/fixed-tracker-payments/constants';
import {
  ADMIN_AFFILIATE_TRACKERS_SETTINGS_TRACKERS_DEAL_MAIN_ROUTE_NAME,
} from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/constants';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';
import { useTableData } from '@modules/tables/composables';
import { versionSplitterTags } from '@modules/tables/utils/formatters';
import { isString } from '@utils/guards';

import VBulkChangingFixedPayment from '../../components/VBulkChangingFixedPayment/VBulkChangingFixedPayment.vue';
import VBulkChangingTrafficSource from '../../components/VBulkChangingTrafficSource/VBulkChangingTrafficSource.vue';
import VBulkEditTrackerCountry from '../../components/VBulkEditTrackerCountry/VBulkEditTrackerCountry.vue';
import VBulkReplacementDeals from '../../components/VBulkReplacementDeals/VBulkReplacementDeals.vue';
import VChangeTrafficSource from '../../components/VChangeTrafficSource/VChangeTrafficSource.vue';
import VCreateEditTracker from '../../components/VCreateEditTracker/VCreateEditTracker.vue';
import VEditTrackerCountry from '../../components/VEditTrackerCountry/VEditTrackerCountry.vue';
import { useTrackerTableActions } from '../../composables';
import {
  AFFILIATE_TRACKERS_SETTINGS_LIST_QUERY,
  AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS,
} from '../../constants';
import { useAffiliateTrackersListQuery } from '../../queries';
import { useAffiliateTrackersStore } from '../../store';

const route = useRoute();
const trackersStore = useAffiliateTrackersStore();
const trackersTableState = toRef(trackersStore, 'trackersTableState');
const { updateTrackersTableState } = trackersStore;
const { hasPermission } = usePermissionsStore();
const { getTranslation } = useI18n();

const affiliateId = computed<number>(() => (isString(route.params.id) ? Number(route.params.id) : 0));

const enableListQuery = ref(false);
const showBulkReplacementDealsDialog = ref(false);
const showBulkChangingFixedPaymentsDialog = ref(false);
const showBulkChangeTrackerCountryDialog = ref(false);
const showBulkChangingTrafficSourceDialog = ref(false);
const selectedIds = ref<number[]>([]);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/affiliates/trackers',
  actionForFilter: 'list',
  filterName: 'tracker',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  tracker_code: {
    type: ESmartFilterColumnType.COMPARE,
    defaultMobilePreview: true,
  },
  assigning_at: {
    type: ESmartFilterColumnType.DATE,
  },
  description: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  type_of_transaction: {
    type: ESmartFilterColumnType.CHECKBOX,
    disableCheckboxFilter: true,
    localization: {
      translationPath: 'admin.partnership_program.conditions.create_edit.main_params.form.',
    },
  },
  country_code: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  country: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  traffic_source_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  hotc_condition_title: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  hotc_condition_id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  hotc_condition_version: {
    type: ESmartFilterColumnType.CHECKBOX,
    formatValue: versionSplitterTags,
  },
};

const multiselectOptions = computed<ITableMultiselectActions>(() => {
  const actions: ITableMultiselectActions = {};

  if (hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.UPDATE_COUNTRY).value) {
    actions.bulkEditCountry = {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_TRACKER_COUNTRY').value,
      icon: 'pi-flag',
    };
  }

  if (hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.CHANGE_TRAFFIC_SOURCE).value) {
    actions.bulkChangingTrafficSource = {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_TRAFFIC_SOURCE').value,
      icon: 'pi-sitemap',
    };
  }

  if (hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT).value) {
    actions.bulkReplacementDeals = {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_REPLACEMENT_DEALS').value,
      icon: 'pi-cog',
    };
  }

  if (hasPermission(AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_EDIT).value) {
    actions.bulkChangingFixedPayments = {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_CHANGING_FIXED_PAYMENT').value,
      icon: 'pi-wallet',
    };
  }

  return actions;
});

const {
  tableState,
  filterSetId,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateTrackersListQuery, affiliateId, enableListQuery);

const {
  showCreateEditTrackerDialog,
  showEditCountryDialog,
  showChangeTrafficSourceDialog,
  isTrackerCreating,
  trackerId,
  trafficSourceId,
  trackerDescription,
  tableActions,
  onTableActionItemSelect,
  onOpenTrackerDialog,
  onHideCreateEditDialog,
} = useTrackerTableActions(
  data,
  ADMIN_AFFILIATE_TRACKERS_SETTINGS_TRACKERS_DEAL_MAIN_ROUTE_NAME,
  ADMIN_AFFILIATE_TRACKERS_FIXED_PAYMENTS_MAIN_ROUTE_NAME,
);

function handleMultiselectAction(data: ITableMultiselectActionParams): void {
  selectedIds.value = data.selection;

  switch (data.actionId) {
    case 'bulkChangingTrafficSource':
      showBulkChangingTrafficSourceDialog.value = true;
      break;
    case 'bulkReplacementDeals':
      showBulkReplacementDealsDialog.value = true;
      break;
    case 'bulkChangingFixedPayments':
      showBulkChangingFixedPaymentsDialog.value = true;
      break;
    case 'bulkEditCountry':
      showBulkChangeTrackerCountryDialog.value = true;
      break;
    default:
      break;
  }
}

onMounted(() => {
  updateTableState(trackersTableState.value);
  enableListQuery.value = true;
});

onUnmounted(() => {
  updateTrackersTableState(tableState.value);
});
</script>

<template>
  <VCard v-data-qa-auto-id="'TrackersSettingsListPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_HEADER')"
      :filter-props="tableFilterOptions"
      :multiselect="multiselectOptions"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @multiselect-action="handleMultiselectAction"
      @refresh="refreshTableData"
    >
      <template #tabs>
        <VAffiliateTrackersSettingsTabs :bottom-indent="false" />
      </template>
      <template #recordAction="{ listItem }">
        <VTableMultiselectActions
          :list-item="listItem"
          :options="tableActions"
          :disabled="false"
          static
          @select="onTableActionItemSelect"
        />
      </template>
      <template #footerButtons>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_ADD_TRACKER')"
          @click="onOpenTrackerDialog"
        />
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS, 'TRACKER_SETTINGS_BACK')"
          skip-history
          :to="{
            name: ADMIN_AFFILIATES_APPROVED_LIST_ROUTE_NAME,
          }"
        />
      </template>
    </VTable>
  </VCard>

  <VBulkReplacementDeals
    v-model:visible="showBulkReplacementDealsDialog"
    :aff-id="affiliateId"
    :selected-ids="selectedIds"
    :filter-id="filterSetId"
    :page="tableState?.page"
  />

  <VCreateEditTracker
    v-model:visible="showCreateEditTrackerDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :tracker-description="trackerDescription"
    :trackers-list-query-key="AFFILIATE_TRACKERS_SETTINGS_LIST_QUERY"
    :is-tracker-creating="isTrackerCreating"
    @hide="onHideCreateEditDialog"
  />

  <VEditTrackerCountry
    v-model:visible="showEditCountryDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :trackers-list-query-key="AFFILIATE_TRACKERS_SETTINGS_LIST_QUERY"
    @hide="onHideCreateEditDialog"
  />

  <VBulkEditTrackerCountry
    v-model:visible="showBulkChangeTrackerCountryDialog"
    :aff-id="affiliateId"
    :selected-ids="selectedIds"
    :filter-id="filterSetId"
    :page="tableState?.page"
    :trackers-list-query-key="AFFILIATE_TRACKERS_SETTINGS_LIST_QUERY"
    @hide="onHideCreateEditDialog"
  />

  <VBulkChangingFixedPayment
    v-model:visible="showBulkChangingFixedPaymentsDialog"
    :aff-id="affiliateId"
    :selected-ids="selectedIds"
    :filter-id="filterSetId"
    :page="tableState?.page"
  />

  <VBulkChangingTrafficSource
    v-model:visible="showBulkChangingTrafficSourceDialog"
    :aff-id="affiliateId"
    :selected-ids="selectedIds"
    :filter-id="filterSetId"
    :page="tableState?.page"
  />

  <VChangeTrafficSource
    v-model:visible="showChangeTrafficSourceDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :traffic-source-id="trafficSourceId"
    :trackers-list-query-key="AFFILIATE_TRACKERS_SETTINGS_LIST_QUERY"
    @hide="onHideCreateEditDialog"
  />

  <RouterView />
</template>
